import { useTranslation } from "react-i18next";
import { useCallback, useMemo } from "react";
import type { SupportedLanguage } from "@/config/i18n";
import {
  SUPPORTED_LANGUAGES,
  LANGUAGE_CONFIG,
  DEFAULT_LANGUAGE,
} from "@/config/i18n";

/**
 * 规范化语言代码
 * 将 zh-CN, zh-TW 等转换为 zh，en-US 转换为 en
 */
function normalizeLanguage(lang: string): SupportedLanguage {
  const baseLang = lang.split("-")[0].toLowerCase();
  if (SUPPORTED_LANGUAGES.includes(baseLang as SupportedLanguage)) {
    return baseLang as SupportedLanguage;
  }
  return DEFAULT_LANGUAGE;
}

/**
 * 语言切换 Hook
 * 提供当前语言、切换语言、语言列表等功能
 */
export function useLanguage() {
  const { i18n } = useTranslation();

  // 获取当前语言（规范化处理）
  const currentLanguage = useMemo(
    () => normalizeLanguage(i18n.language),
    [i18n.language]
  );

  // 切换语言
  const changeLanguage = useCallback(
    async (lang: SupportedLanguage) => {
      try {
        await i18n.changeLanguage(lang);
        // 可选：通知后端用户的语言偏好
        // await updateUserLanguagePreference(lang)
      } catch (error) {
        console.error("Failed to change language:", error);
      }
    },
    [i18n]
  );

  // 获取语言配置
  const getLanguageConfig = useCallback(
    (lang: SupportedLanguage) => LANGUAGE_CONFIG[lang],
    []
  );

  // 当前语言配置
  const currentLanguageConfig = getLanguageConfig(currentLanguage);

  return {
    // 当前语言代码
    currentLanguage,
    // 当前语言配置（标签、图标等）
    currentLanguageConfig,
    // 切换语言函数
    changeLanguage,
    // 所有支持的语言
    supportedLanguages: SUPPORTED_LANGUAGES,
    // 获取语言配置
    getLanguageConfig,
    // i18n 实例（用于高级用法）
    i18n,
  };
}
